import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ShieldCheck, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/constants";
import useSWR from "swr";

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  walletAddress: string | null;
  role: UserRole;
  createdAt: string;
  lastLoginAt: string | null;
  emailVerified: boolean;
}

interface AdminUsersResponse {
  data: AdminUser[];
  meta: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
}

type UsersState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
};

const defaultUsersState: UsersState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
};

const formatWalletAddress = (address: string | null) => {
  if (!address) return "Not connected";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export default function UsersPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();

  useEffect(() => {
    document.title = "Users | SECH Admin";
  }, []);

  const [pageState, setPageState] = usePagePersistentState<UsersState>("users", defaultUsersState);
  const searchQuery = pageState.searchQuery;
  const currentPage = pageState.currentPage;
  const itemsPerPage = pageState.itemsPerPage;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "users_table",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  const [verifyingUser, setVerifyingUser] = useState<AdminUser | null>(null);
  const [verifyingUserId, setVerifyingUserId] = useState<string | null>(null);

  const { data: usersResponse, error, mutate } = useSWR<AdminUsersResponse>(
    "/v1/admin/users",
    async () => {
      const params = new URLSearchParams();
      params.append("pageSize", "500");
      const response = await posApiFetch(`/v1/admin/users?${params.toString()}`);
      return response.json();
    }
  );

  const usersData = usersResponse?.data ?? [];

  const handleVerifyUser = async () => {
    if (!verifyingUser) return;
    const user = verifyingUser;
    setVerifyingUserId(user.id);
    setVerifyingUser(null);
    try {
      const response = await posApiFetch(`/v1/admin/users/${user.id}/verify`, {
        method: "PATCH",
      });
      if (!response.ok) throw new Error("Failed to verify user");
      toast({ title: "User verified", description: `${user.email} has been manually verified.` });
      mutate();
    } catch (error) {
      toast({
        title: "Failed to verify user",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setVerifyingUserId(null);
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Email", field: "email", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-medium">{value}</span>
      ),
    },
    {
      label: "Name", field: "displayName", sortable: true,
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "N/A"}</span>
      ),
    },
    {
      label: "Wallet", field: "walletAddress",
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-mono">{formatWalletAddress(value)}</span>
      ),
    },
    {
      label: "Role", field: "role", sortable: true,
      cellTemplate: (value: string) => (
        <Badge variant={value === UserRole.ADMIN ? "default" : "secondary"} className="text-[11px]">
          {value}
        </Badge>
      ),
    },
    {
      label: "Joined", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Last Login", field: "lastLoginAt", sortable: true,
      cellTemplate: (value: string | null) => value ? formatDate(value) : "N/A",
    },
    {
      label: "Verified", field: "emailVerified",
      cellTemplate: (value: boolean) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${value ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${value ? "bg-green-800" : "bg-gray-500"} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value ? "Yes" : "No"}</span>
        </span>
      ),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: string, row: AdminUser) => {
        if (verifyingUserId === row.id) {
          return <Loader2 className="h-4 w-4 animate-spin text-[var(--secondary-text-color)]" />;
        }
        return (
          <ActionTemplate
            row={row}
            actions={[
              {
                label: "Verify Email",
                icon: ShieldCheck,
                onClick: (r: AdminUser) => setVerifyingUser(r),
              },
            ]}
          />
        );
      },
    },
  ], [verifyingUserId]);

  const filteredUsers = useMemo(() => {
    const search = debouncedSearch.toLowerCase();
    if (!search) return usersData;
    return usersData.filter((user) =>
      user.email?.toLowerCase().includes(search)
      || user.displayName?.toLowerCase().includes(search)
      || user.walletAddress?.toLowerCase().includes(search)
      || user.role?.toLowerCase().includes(search)
    );
  }, [usersData, debouncedSearch]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a: AdminUser, b: AdminUser) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "email": aVal = a.email ?? ""; bVal = b.email ?? ""; break;
        case "displayName": aVal = a.displayName ?? ""; bVal = b.displayName ?? ""; break;
        case "role": aVal = a.role ?? ""; bVal = b.role ?? ""; break;
        case "createdAt":
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case "lastLoginAt":
          aVal = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
          bVal = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
          break;
        default:
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedUsers.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setPageState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setPageState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedUsers.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Users</h2>
          <p className="text-[var(--secondary-text-color)] mb-4">Failed to load users</p>
          <Button onClick={() => mutate()} className="bg-[var(--primary-color)] text-white">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">
          Users
          {usersResponse?.meta?.total !== undefined && (
            <span className="text-[13px] font-normal text-[var(--secondary-text-color)] ml-2">
              ({usersResponse.meta.total} total)
            </span>
          )}
        </p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input
              className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center"
              type="text"
              placeholder="Search by email, name, or wallet"
              value={searchQuery ?? ""}
              onChange={(e) => setPageState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))}
            />
          </div>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedUsers.length && !usersResponse ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
            </div>
          ) : !paginatedUsers.length ? (
            <NoRecordFound title="Users" description="There are no users matching your search." />
          ) : (
            <CommonGrid
              rowData={paginatedUsers}
              isLoading={!usersResponse}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!verifyingUser}
        onClose={() => setVerifyingUser(null)}
        onConfirm={handleVerifyUser}
        title="Verify User"
        description={`Are you sure you want to manually verify ${verifyingUser?.email}? This will mark their email as verified.`}
        confirmText="Verify"
        cancelText="Cancel"
      />
    </div>
  );
}
